import { findWaypoints } from "src/utils/maps"
import { saveToLocalStorage } from "src/localStorage"

const closeModal = (state, action) => {
  const modalClosed = action.payload
  if (modalClosed === "CityModal") {
    // The selection outlives the modal: closing it drops you back to the map with the same cell
    // still under the action bar, which is where you opened it from.
    state.cityModalOpen = false
  } else if (modalClosed === "AttackModal") {
    // The modal is the troops-and-confirm half of a march; closing it (Esc, "back to the map")
    // returns to drawing the route with the path kept. Giving up altogether is `cancelMovement`.
    state.attackModalOpen = false
  }
  return state
}

// Abandon the march: no path, no origin, the bar goes back to the selection.
const cancelMovement = (state) => {
  state.pickingOrigin = false
  state.selectedCityForAction = null
  state.selectedNodes = []
  state.creatingMovement = false
  state.attackModalOpen = false
  state.movementIntent = "move"
  state.movementTroops = {}
  return state
}

// The force for the march being drafted (Q2). Written by the picker as the player drags, so the
// route step — drawn with the modal closed — knows the stack's pace and can price the trip.
const setMovementTroops = (state, action) => {
  state.movementTroops = action.payload || {}
  return state
}

// The route is drawn; on to the troops.
const openMovementModal = (state) => {
  if (state.creatingMovement) state.attackModalOpen = true
  return state
}

const clickCity = (state, action) => {
  if (state.strikeOrigin) {
    // Targeting is resolved by the component that started it; clear the mode here so a stray
    // click cannot leave the map stuck in it.
    state.strikeTarget = action.payload
    state.strikeOrigin = null
    return state
  }
  if (state.creatingMovement) {
    return _addMovementNode(state, action)
  }

  /* The sea selects like any other cell (03/D2, 11/F6b, owner 2026-09-08). Water is legal
     terrain at double cost, so a slow march pauses on a sea cell while it saves the points to
     cross it — and the canvas has drawn that stack since S10b. A click on it used to be thrown
     away here, which left a cell with a visible army on it reading as dead. The action bar
     already knows what a sea cell cannot do (`cellActions` answers "not land" for founding and
     marching); refusing the selection was the client's rule, not the server's.

     Selecting a cell no longer throws the whole city modal over the board. The map action bar
     reads this, and offers the modal behind its CITY button — and a second click on the cell
     already under the bar means the same as pressing that button, under the same rule: only where
     a city view exists (the click hook says so via `hasCity`).

     `open` is how a caller that has no action bar says "there is no second step here". The replay
     is the one (2026-09-04): the two-step rule exists so a click does not cover the board while
     you are playing, and the bar is what the first step is *for* — without it a first click looked
     like nothing happening at all. The decision is the caller's rather than a flag read from
     another slice, because the reducer cannot see which view mounted it. */
  const { col, row, hasCity, open } = action.payload
  // A pending "march here" belongs to the cell it was pressed on; any click on the map ends it.
  state.pickingOrigin = false
  const again =
    state.selectedCity != null &&
    Number(state.selectedCity.col) === Number(col) &&
    Number(state.selectedCity.row) === Number(row)
  state.selectedCity = { col, row }
  if ((again || open) && hasCity && !state.cityModalOpen) {
    state.cityModalOpen = true
    state.cityModalSeq += 1
  }
  return state
}

// A city chosen from the roster rather than the map: bring the map to it and put it under the
// action bar. A second click on the row already selected opens the full city view (owner's R19,
// 2026-09-03) — the same two-step rule a map click follows, so a row never throws the whole
// sheet over the board on the first press. Mid-path or mid-targeting the roster click means the
// same as a map click would, so those modes still win.
const focusCity = (state, action) => {
  state.focusedCity = { ...action.payload, seq: (state.focusedCity?.seq ?? 0) + 1 }
  if (state.strikeOrigin || state.creatingMovement) {
    return clickCity(state, action)
  }
  const { col, row, open } = action.payload
  state.pickingOrigin = false
  const again =
    state.selectedCity != null &&
    Number(state.selectedCity.col) === Number(col) &&
    Number(state.selectedCity.row) === Number(row)
  state.selectedCity = { col: Number(col), row: Number(row) }
  // `open` skips the two-step rule for a caller with no action bar — see `clickCity` above.
  if ((again || open) && !state.cityModalOpen) {
    state.cityModalOpen = true
    state.cityModalSeq += 1
  }
  return state
}

// A teammate's pin (or a coordinate clicked in chat): travel there and put the cell under the
// action bar — the mini modal — without throwing the full city sheet over the map.
const pingCity = (state, action) => {
  const coords = { col: Number(action.payload.col), row: Number(action.payload.row) }
  state.pickingOrigin = false
  state.selectedCity = coords
  state.focusedCity = { ...coords, seq: (state.focusedCity?.seq ?? 0) + 1 }
  return state
}

const openCityModal = (state) => {
  state.cityModalOpen = true
  state.cityModalSeq += 1
  return state
}

const clearSelectedCity = (state) => {
  state.selectedCity = null
  state.cityModalOpen = false
  state.pickingOrigin = false
  return state
}

// "March here": the bar lists the garrisons to march from. Only with a cell selected — the list is
// the selected cell's.
const startPickingOrigin = (state) => {
  if (state.selectedCity) state.pickingOrigin = true
  return state
}

const stopPickingOrigin = (state) => {
  state.pickingOrigin = false
  return state
}

// The pointer rested on a cell: remember which, and where the pointer was, for the tooltip. Not
// while the city modal covers the map; while a path is being drawn the names help, so that stays.
const hoverCity = (state, action) => {
  if (state.cityModalOpen) return state
  state.hoveredCity = action.payload
  return state
}

const clearHover = (state) => {
  state.hoveredCity = null
  return state
}

const startMovementFromCity = (state, action) => {
  state.movementIntent = "move"
  return _startPathFromCity(state, action)
}

// A march is two halves. First the route is drawn on the map itself — nothing may cover the map
// then, so the modal stays closed and a planner bar takes the action bar's place. Only once the
// path exists does the modal open, for the troops and the confirmation.
// A march *to* a cell: the target was picked first, then the origin among the player's garrisons.
// Both ends are known, so the route starts pre-drawn (origin last, as always) and the planner opens
// with "Choose troops" already available; waypoints can still be added, or the route reset.
const startMovementToCity = (state, action) => {
  const { origin, target } = action.payload
  state.pickingOrigin = false
  state.movementIntent = "move"
  // Units first here too (Q2): both ends are known, so after the picker the march is ready to send.
  state.attackModalOpen = true
  state.creatingMovement = true
  state.movementTroops = {}
  state.selectedCityForAction = origin
  state.selectedCity = null
  state.selectedNodes = [
    { col: Number(target.col), row: Number(target.row) },
    { col: Number(origin.col), row: Number(origin.row) },
  ]
  return state
}

// Colonize (owner, 2026-09-07): the founding party *is* the force — the deed's workers, or one
// body for the horde — picked by `cellActions.colonizingPartyFor` before the press, so the picker
// is skipped and the map is the next step. Otherwise an ordinary march: sent as `move_troops`,
// and on arrival the player still presses Settle. Only the planner's wording reads the intent;
// `cancelMovement` puts it back to "move".
const startColonizationFromCity = (state, action) => {
  const { col, row, troops } = action.payload
  state.movementIntent = "colonize"
  state.pickingOrigin = false
  state.attackModalOpen = false
  state.creatingMovement = true
  state.selectedCityForAction = { col, row }
  state.selectedCity = null
  state.movementTroops = { ...(troops || {}) }
  state.selectedNodes = [{ col: Number(col), row: Number(row) }]
  return state
}

const _startPathFromCity = (state, action) => {
  state.pickingOrigin = false
  // Units first, then the route (owner's Q2): the picker opens at once, and the route step that
  // follows knows the stack's pace — so it can say how many rounds the trip takes as it is drawn.
  state.attackModalOpen = true
  state.creatingMovement = true
  state.selectedCityForAction = action.payload
  state.selectedNodes = []
  state.selectedCity = null
  state.movementTroops = {}
  state = _addMovementNode(state, action)
  return state
}

// `selectedNodes` keeps the origin LAST and the newest waypoint FIRST (expandNodesToPath and the
// modal reverse it to walk origin → target). So each click goes to the front: the route is walked
// in the order it was clicked, and the last cell clicked is where the march ends.
//
// Three rules from the 2026-09-03 playtest, one amended by the 2026-09-05 one:
// - R15: a click that does not line up with the last node is not refused — the simplest dry
//   route to it is found and its corners become the waypoints;
// - S15 (supersedes R15's truncation): a click on a cell already on the route adds it again —
//   routes may revisit a cell (R7) — and only a click on the route's *current end* takes a leg
//   back, to the node before it. Cutting back to any re-clicked node threw away everything the
//   player had drawn past it, for a click that meant "come back through here";
// - R14: a click that merely extends the last straight leg replaces its end rather than
//   leaving a redundant node in the middle of the line.
const _addMovementNode = (state, action) => {
  const node = { col: Number(action.payload.col), row: Number(action.payload.row) }
  const previous = state.selectedNodes[0]
  if (!previous) {
    state.selectedNodes.unshift(node)
    return state
  }
  if (previous.col === node.col && previous.row === node.row) {
    // The end clicked again undoes the last leg. The origin is the one node that never goes:
    // a route with nowhere to start is no route.
    if (state.selectedNodes.length > 1) state.selectedNodes.shift()
    return state
  }

  const isOrthogonal = node.col === previous.col || node.row === previous.row
  if (isOrthogonal) {
    const before = state.selectedNodes[1]
    if (before) {
      const sameLine =
        (before.col === previous.col && previous.col === node.col && Math.sign(node.row - previous.row) === Math.sign(previous.row - before.row)) ||
        (before.row === previous.row && previous.row === node.row && Math.sign(node.col - previous.col) === Math.sign(previous.col - before.col))
      if (sameLine) {
        state.selectedNodes[0] = node
        return state
      }
    }
    state.selectedNodes.unshift(node)
    return state
  }

  const waypoints = findWaypoints(previous, node)
  if (!waypoints) return state
  // Walking order is previous → … → node; each unshift puts the next step in front, so the
  // list ends newest first with the clicked cell at the head.
  waypoints.forEach((waypoint) => state.selectedNodes.unshift(waypoint))
  return state
}

const resetMovementNodes = (state) => {
  state.selectedNodes = [state.selectedNodes[state.selectedNodes.length - 1]]
  return state
}

const startStrikeFromCity = (state, action) => {
  state.pickingOrigin = false
  state.strikeOrigin = action.payload
  state.strikeTarget = null
  state.cityModalOpen = false
  state.selectedCity = null
  return state
}

const clearStrike = (state) => {
  state.strikeOrigin = null
  state.strikeTarget = null
  return state
}

// Remember the order the player dragged their cities into. Persisted like the login tokens are
// (see store/user/reducers.js): a per-browser preference, so localStorage is its home.
const setCityOrder = (state, action) => {
  const gameId = String(action.payload.gameId)
  const order = [...action.payload.order]
  state.cityOrder = { ...state.cityOrder, [gameId]: order }
  saveToLocalStorage({ cityOrder: state.cityOrder })
  return state
}

/* A row the player never placed goes to the end, in the order it first appeared (requirement 36,
   R1). The roster dispatches the ids it is showing that the order has no rank for, and they are
   appended — so a camp made by a march landing on open ground is the last row, and stays where it
   was put when the next one lands, across renders and reloads. Nothing already ranked moves, and
   when there is nothing new nothing is written: the stored shape stays lists of cells
   (`sanitizeStoredData`), and the dragged order (`setCityOrder`) is untouched. */
const rememberCityRows = (state, action) => {
  const gameId = String(action.payload.gameId)
  const current = state.cityOrder[gameId] || []
  const known = new Set(current)
  const added = []
  const ids = action.payload.ids || []
  ids.forEach((id) => {
    if (typeof id !== "string" || known.has(id)) return
    known.add(id)
    added.push(id)
  })
  if (!added.length) return state
  state.cityOrder = { ...state.cityOrder, [gameId]: [...current, ...added] }
  saveToLocalStorage({ cityOrder: state.cityOrder })
  return state
}

/* A wave's arrivals have been taken in hand (SV12): remembered as toured *before* the camera
   moves, not after, so nothing can re-tour them — a second payload for the same round arrives
   while the first newcomer is still on screen. */
const spawnTourStarted = (state, action) => {
  const keys = action.payload || []
  state.spawnTour.toured = [...new Set([...state.spawnTour.toured, ...keys])]
  state.spawnTour.running = true
  return state
}

const spawnTourEnded = (state) => {
  state.spawnTour.running = false
  return state
}

/* Which way the stacks of the round that just played ended up facing (`roundMotion.finalFacings`),
   merged over what was known: a later show turns a camp, nothing else does. Bounded to the newest
   `MAX_FACINGS` a game, since every camp a game ever had would otherwise stay in storage. */
const MAX_FACINGS = 300
const rememberFacings = (state, action) => {
  const gameId = String(action.payload.gameId)
  const facings = action.payload.facings || {}
  if (!Object.keys(facings).length) return state
  const current = { ...(state.stackFacing?.[gameId] || {}) }
  Object.entries(facings).forEach(([key, angle]) => {
    if (!Number.isFinite(angle)) return
    delete current[key]
    current[key] = angle
  })
  const kept = Object.entries(current).slice(-MAX_FACINGS)
  state.stackFacing = { ...(state.stackFacing || {}), [gameId]: Object.fromEntries(kept) }
  saveToLocalStorage({ stackFacing: state.stackFacing })
  return state
}

const reducers = {
  rememberFacings,
  spawnTourStarted,
  spawnTourEnded,
  closeModal,
  setCityOrder,
  rememberCityRows,
  openCityModal,
  clearSelectedCity,
  startPickingOrigin,
  stopPickingOrigin,
  startStrikeFromCity,
  clearStrike,
  clickCity,
  focusCity,
  pingCity,
  hoverCity,
  clearHover,
  startMovementFromCity,
  startMovementToCity,
  startColonizationFromCity,
  resetMovementNodes,
  cancelMovement,
  setMovementTroops,
  openMovementModal,
}

export default reducers
